<script setup lang="ts">
import slug from 'limax'
import RatingHeader from '~/components/rating/RatingHeader.vue'
import type { ICompanyData, ISeoHeader } from '~/types/rating'
import { useRating } from '~/composables/useRating'
import { showModal as showModalTop } from '~/store/modal/TopModal'
import { showModal as showModalAudit } from '~/store/modal/AuditModal'
import { showModal as showModalCode } from '~/store/modal/CodeModal'

interface Props {
  contrastBackground?: boolean
}

withDefaults(defineProps<Props>(), {
  contrastBackground: false,
})

const route = useRoute()
const {
  public: { BASE_URL },
} = useRuntimeConfig()
const { $gtm } = useNuxtApp()

const { fetchOrganization, fetchWordForms, fetchCities, cities, city, fetchTypes, companyTypes }
  = useRating()
const companyData: ICompanyData = await fetchOrganization()

if (companyData === "NOT_FOUND") {
  await navigateTo('/rating', {
    redirectCode: 301,
    external: true,
  })
} else if (Array.isArray(companyData)) {
  await navigateTo(companyData[0], companyData[1])
}

const wordForms = await fetchWordForms(
  companyData.organization.city_id.toString(),
  companyData.organization.rating_type_id.toString(),
)

const organizationInRating = companyData.nearbyOrganizations[0]
const ratingSeoHeader: ISeoHeader = {
  title: {
    meta: `${wordForms.type.shortWordForm.nominative} ${companyData.organization.name} на ${companyData.organization.address}: отзывов: ${organizationInRating?.reviews_count}, рейтинг: ${organizationInRating?.rating_value}`,
    content: `Рейтинг ${wordForms.type.shortWordForm.genitive.toLowerCase()} ${companyData.organization.name} ${companyData.organization.metro ? `у м. ${companyData.organization.metro}` : ''} и соседей`,
  },
  description: {
    meta: `${wordForms.type.shortWordForm.nominative} ${companyData.organization.name} в ${wordForms.city.regularWordForm.prepositional} по адресу ${companyData.organization.address}, рейтинг: ${organizationInRating?.rating_value.toFixed(1)}, отзывы: ${organizationInRating?.reviews_count}, сравните с соседними компаниями в этом же районе.`,
    content: [
      `На странице мы собрали 20 компаний вблизи от ${companyData.organization.name} по ${companyData.organization.address} — это ваши реальные конкуренты. Их люди будут сравнивать с вами, когда будут искать компанию рядом с домом или работой в картах. Текущее кол-во ваших отзывов: ${organizationInRating?.reviews_count}, место в рейтинге: ${organizationInRating?.rating_value}. ${organizationInRating?.place_in_rating > 1 ? `Кто на первом месте? ${companyData.nearbyOrganizations[0].name}, ${companyData.nearbyOrganizations[0].metro} - ${companyData.nearbyOrganizations[0].reviews_count} отзывов.` : ''}`,
      'Чем больше у вас отзывов и чем выше рейтинг, тем больше шансов привлечь клиента.'
    ],
  },
}

useHead({
  title: ratingSeoHeader.title.meta,
  link: [{
    rel: 'canonical',
    href: `${BASE_URL}rating/org/${companyData.organization.slug}`,
  }],
})
useServerSeoMeta({
  title: ratingSeoHeader.title.meta,
  description: ratingSeoHeader.description.meta,
})

city.value = companyData.organization.city_id
await fetchCities()
await fetchTypes()

const seoCompanyType = companyTypes.value.find(
  el => el.id === Number(companyData.organization.rating_type_id),
)
const seoCity = cities.value.find(el => el.id === Number(companyData.organization.city_id))

function getGetIframe() {
  showModalCode(route.params.slug[0])
  $gtm.sendAnalytics(
    'rating_widget_code_open',
    'click',
    'Rating',
    'Получить код для сайта',
  )
}
</script>

<template>
  <section>
    <ol class="hidden" itemscope itemtype="https://schema.org/BreadcrumbList">
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <a href="/" itemprop="item">
          <span itemprop="name">Главная</span>
        </a>
        <meta itemprop="position" content="1">
      </li>
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <a href="/rating" itemprop="item">
          <span itemprop="name">Рейтинг</span>
        </a>
        <meta itemprop="position" content="2">
      </li>
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <a
          :href="`/rating/${companyData.organization.city_id}/${companyData.organization.rating_type_id}/${slug(seoCity.name)}/${slug(seoCompanyType.name)}`"
          itemprop="item"
        >
          <span itemprop="name">{{ wordForms.type.regularWordForm.nominative }} в {{ wordForms.city.regularWordForm.prepositional }}</span>
        </a>
        <meta itemprop="position" content="3">
      </li>
      <li
        itemprop="itemListElement" itemscope
        itemtype="https://schema.org/ListItem"
      >
        <a :href="`/rating/org/${route.params.slug[0]}`" itemprop="item">
          <span itemprop="name">{{ companyData.organization.name }}</span>
        </a>
        <meta itemprop="position" content="4">
      </li>
    </ol>
    <div :class="{ 'bg-milky-200': contrastBackground }" class="w-full">
      <div
        class="max-w-[1012px] w-full px-4 mx-auto py-1 md:py-[40px] flex flex-col gap-6 md:gap-10"
      >
        <RatingHeader
          :title="ratingSeoHeader.title.content"
          :description="ratingSeoHeader.description.content"
        />
        <div class="flex flex-col gap-1">
          <h3 class="text-h3">
            {{ companyData.organization.name }}
          </h3>
          <p class="text-primary-large">
            {{ companyData.organization.address }}
          </p>
          <NuxtLink
            :to="`http://${companyData.organization.website}`"
            target="_blank"
            class="w-fit cursor-pointer text-blue-500 border-b-[1px] border-blue-500 hover:border-blue-400 duration-150 ease-out text-primary-large"
          >
            {{ companyData.organization.website }}
          </NuxtLink>

          <template v-if="companyData.organization.top">
            <div
              v-if="companyData.organization.top.percent < 80"
              class="flex md:hidden w-[350px] flex-col gap-5 mt-10 relative"
            >
              <p class="text-h3 w-3/4">
                Входит в ТОП {{ companyData.organization.top.percent }}%<br>{{ wordForms.type.regularWordForm.genitive.toLowerCase() }}
              </p>
              <div class="flex flex-col gap-3 z-20">
                <button
                  class="w-full lg:w-5/6 py-4 text-btn-text bg-gradient-to-b from-[#22C275] to-green-500 text-white hover:opacity-90 duration-150 rounded-md"
                  @click="showModalTop()"
                >
                  Получить сертификат
                </button>
                <button
                  class="w-fit lg:w-5/6 py-2 px-6 text-m-primary bg-gradient-to-b from-[#22C275] to-green-500 text-white hover:opacity-90 duration-150 rounded-md"
                  @click="getGetIframe"
                >
                  Получить код для сайта
                </button>
              </div>

              <NuxtImg
                
                src="/certificate.png"
                class="block absolute h-40 -top-4 -right-2 w-[7.3rem] rotate-6 rounded-xl z-10"
                alt="certificate image"
              />
            </div>
          </template>
        </div>

        <div class="flex flex-col md:flex-row gap-10 show">
          <div
            v-if="companyData.nearbyOrganizations"
            class="w-full md:w-2/3 overflow-hidden"
          >
            <table
              v-if="companyData.nearbyOrganizations.length > 0"
              class="w-full duration-150"
            >
              <thead>
                <tr>
                  <th class="text-primary p-3 md:block hidden" />
                  <th class="text-primary p-3" />
                  <th class="text-m-primary md:text-primary text-end">
                    Оценка
                  </th>
                  <th
                    class="text-m-primary md:text-primary p-3 pr-4 text-end items-start duration-150"
                    :class="{
                      'text-blue-500 underline decoration-dotted cursor-pointer':
                        !('slug' in route.params),
                    }"
                  >
                    Отзывы
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="(item, index) in companyData.nearbyOrganizations"
                  :key="item.id"
                >
                  <tr
                    v-if="index % 20 === 19"
                  >
                    <td colspan="4">
                      <div class="w-full self-start block md:hidden my-1">
                        <div class="flex flex-col gap-7">
                          <div class="bg-blue-500 rounded-xl text-white show">
                            <div class="pl-5 pt-8 pr-6 pb-14">
                              <p class="text-h5 tracking-wide font-golos">Аудит карточки на картах</p>
                              <p class="mt-2">
                                Проведем бесплатный аудит ваших филиалов в
                                картах совместно с аналитиком Ранга, определим
                                слабые места и точки роста.
                              </p>
                              <RButton
                                class="text-black mt-6"
                                @click="showModalAudit('')"
                              >
                                Заказать
                              </RButton>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <tr
                    :class="{
                      'bg-yellow-300': item.id === companyData.organization.id,
                      'rounded-tl-lg': index === 0,
                      'rounded-br-lg': index === 4,
                    }"
                    class="show"
                  >
                    <td class="p-2 table-cell md:hidden rounded-tl-lg rounded-bl-lg">
                      <div class="text-sm md:text-base">
                        {{ item.place_in_rating }}
                      </div>
                      <div>
                        <NuxtLink :to="`/rating/org/${item.slug}`">
                          <span
                            class="cursor-pointer text-blue-500 border-b-[1px] border-blue-500 hover:border-blue-400 duration-150 ease-out"
                          >{{ item.name }}</span>
                          <span
                            v-if="item.is_network"
                            class="text-black no-underline"
                          >{{ item.metro ? `, ${item.metro}` : null }}</span>
                        </NuxtLink>
                      </div>
                    </td>

                    <!-- Отдельные столбцы для десктопной версии -->
                    <td
                      class="p-2 md:table-cell hidden"
                      :class="{
                        'rounded-tl-lg': index === 0,
                        'rounded-bl-lg': index === 4,
                      }"
                    >
                      {{ item.place_in_rating }}
                    </td>
                    <td class="py-2 hidden md:block">
                      <NuxtLink :to="`/rating/org/${item.slug}`">
                        <span
                          class="cursor-pointer text-blue-500 border-b-[1px] border-blue-500 hover:border-blue-400 duration-150 ease-out"
                        >{{ item.name }}</span>
                        <span
                          v-if="item.is_network"
                          class="text-black no-underline"
                        >{{ item.metro ? `, ${item.metro}` : null }}</span>
                      </NuxtLink>
                    </td>
                    <td class="py-2 text-end">
                      {{ item.rating_value.toFixed(1) }}
                    </td>
                    <td
                      class="p-2 pr-4 text-end"
                      :class="{
                        'rounded-tr-lg rounded-br-lg': index === 0,
                        'rounded-br-lg': index === 4,
                      }"
                    >
                      {{ item.reviews_count }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <p
              v-else
              class="text-2xl font-bold text-white bg-[#383D44] py-10 rounded-md text-center"
            >
              Данные отсутствуют
            </p>
            <div
              class="flex flex-col md:flex-row gap-1 md:gap-7 items-start md:items-center mt-8 px-3"
            >
              <div class="text-primary-small">
                Все данные из Яндекс.Карт. Если кого-то не хватает или не можете
                найти — пишите на
                <NuxtLink
                  class="text-blue-500 cursor-pointer"
                  to="mailto:art@rang.ai"
                >
                  <span class="border-b border-blue-300">art@rang.ai</span>
                </NuxtLink>
              </div>
            </div>
          </div>
          <div
            v-if="companyData.nearbyOrganizations"
            class="hidden md:block md:w-1/3 sticky self-start"
          >
            <div class="flex flex-col gap-7">
              <template v-if="companyData.organization.top">
                <div
                  v-if="companyData.organization.top.percent < 80"
                  class="hidden md:flex max-w-[350px] flex-col gap-5 mt-10 relative"
                >
                  <p class="text-h3 w-3/4">
                    Входит в ТОП {{ companyData.organization.top.percent }}%<br>{{ wordForms.type.regularWordForm.genitive.toLowerCase() }}
                  </p>
                  <div class="flex flex-col gap-3">
                    <button
                      class="w-full lg:w-5/6 py-4 text-btn-text bg-gradient-to-b from-[#22C275] to-green-500 text-white hover:opacity-90 duration-150 rounded-md"
                      @click="showModalTop()"
                    >
                      Получить сертификат
                    </button>
                    <button
                      class="w-fit py-2 px-6 text-m-primary bg-gradient-to-b from-[#22C275] to-green-500 text-white hover:opacity-90 duration-150 rounded-md z-20"
                      @click="getGetIframe"
                    >
                      Получить код для сайта
                    </button>
                  </div>
                  <NuxtImg
                    
                    src="/certificate.png"
                    class="block absolute h-40 -top-4 -right-2 w-[7.3rem] rotate-6 rounded-xl -z-10"
                    alt="certificate image"
                  />
                </div>
              </template>
              <div class="bg-blue-500 rounded-xl text-white show">
                <div class="pl-5 pt-8 pr-6 pb-14">
                  <p class="text-h5 tracking-wide font-golos">Аудит карточки на картах</p>
                  <p class="mt-2">
                    Проведем бесплатный аудит ваших филиалов в картах совместно
                    с аналитиком Ранга, определим слабые места и точки роста.
                  </p>
                  <RButton class="text-black mt-6" @click="showModalAudit('')">
                    Заказать
                  </RButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss"></style>
